/**
 * The Files section: my files, files shared with me, and the reserved
 * `/files/search/<query>` route (agora#269's shape) the header search's own
 * Files group will open once it exists -- until then this is the only way
 * to reach it, rendered here as a plain filtered list rather than the
 * richer combined-search page `search/SearchResultsPage.tsx` draws for
 * Mail, Chat and Calendar.
 *
 * Runs entirely on `FilesSource` (`source.ts`) -- the fake until
 * `@wtfalch/files` can answer it, see that file's own doc.
 */
import { Button, Callout, Empty, Icon, Tabs } from '@wtfalch/design';
import { formatBytes } from '@wtfalch/utils/format';
import { useEffect, useRef, useState } from 'react';

import type { FilesLocation } from '../routes.ts';
import { Avatar } from '../ui/Avatar.tsx';
import { formatWhen } from '../ui/format.ts';
import { audienceLabel } from './format.ts';
import { roleLabel } from './role.ts';
import type { AgoraFile, Audience, FilesSource, Person, Team } from './source.ts';
import { UploadDialog } from './UploadDialog.tsx';
import './files.css';

const TABS = [
  { id: 'mine', label: 'My files' },
  { id: 'shared', label: 'Shared with me' },
];

/** The design `Icon` set has a picture and a page; nothing finer. */
function fileIcon(name: string): 'image' | 'file' {
  return /\.(png|jpe?g|gif|webp|svg|heic)$/i.test(name) ? 'image' : 'file';
}

export function Files({
  source,
  companyName,
  location,
  onNavigate,
}: {
  source: FilesSource;
  /** For the company-audience label -- "Everyone at <companyName>". */
  companyName: string;
  location: FilesLocation;
  onNavigate: (location: FilesLocation) => void;
}) {
  const [files, setFiles] = useState<readonly AgoraFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [people, setPeople] = useState<readonly Person[]>([]);
  const [teams, setTeams] = useState<readonly Team[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The audience picker's own choices, and Mine's own audience label --
  // loaded once rather than per fetch: the fake's own `people()`/`teams()`
  // are static, and the real SDK's will not change mid-session either.
  useEffect(() => {
    let live = true;
    source
      .people()
      .then((p) => live && setPeople(p))
      .catch(() => {});
    source
      .teams()
      .then((t) => live && setTeams(t))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [source]);

  // `reload` is a plain bump counter, never read in the body -- it exists
  // only to force a refetch after an upload that landed on the tab already
  // open, where `location.tab` itself does not change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const promise =
      location.query !== undefined
        ? source.search(location.query)
        : location.tab === 'shared'
          ? source.shared()
          : source.mine();
    promise.then(
      (result) => {
        if (!live) return;
        setFiles(result);
        setLoading(false);
      },
      (reason: unknown) => {
        if (!live) return;
        setError(reason instanceof Error ? reason : new Error('Files could not load.'));
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [source, location.tab, location.query, reload]);

  const download = (file: AgoraFile) => {
    setDownloadError(null);
    source.downloadUrl(file.id).then(
      (url) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = file.name;
        anchor.click();
      },
      () => setDownloadError(`${file.name} could not download.`),
    );
  };

  // Always lands on Mine, whichever tab (or search) the upload was made
  // from -- "the new file appears at the top of My files" is the point of
  // an upload, not something a person on Shared has to go find. `reload`
  // is what actually re-runs the fetch above when the tab was already
  // `mine` and so the navigate below is a no-op.
  const handleUpload = async (file: File, audience: Audience) => {
    await source.upload(file, audience);
    setUploadFile(null);
    setReload((n) => n + 1);
    onNavigate({ tab: 'mine' });
  };

  const searching = location.query !== undefined;

  return (
    <div className="files contents">
      {/* One flex column, `flex: 1` on the shell body's row -- the same single-
          child trick `today.css`'s own `.today-page` uses under its own
          `display: contents` wrapper. Without it the toolbar and the list
          below would be two flex-row siblings of the row instead of a
          page stacked top to bottom. */}
      <div className="flex flex-1 flex-col min-w-0 min-h-0">
        <div className="flex items-center gap-3 files-toolbar">
          {searching ? (
            <h2 className="h3">Results for “{location.query}”</h2>
          ) : (
            <Tabs
              label="Files"
              value={location.tab}
              onChange={(id) => onNavigate({ tab: id === 'shared' ? 'shared' : 'mine' })}
              tabs={TABS}
            />
          )}
          <div className="grow min-w-0" />
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              e.target.value = '';
              if (file) setUploadFile(file);
            }}
          />
          <Button kind="primary" onPress={() => fileInputRef.current?.click()}>
            <Icon name="upload" size={16} />
            Upload
          </Button>
        </div>

        <div className="flex flex-1 flex-col min-h-0 overflow-y-auto pt-0 px-4 pb-8">
          {downloadError && (
            <Callout tone="bad" timed onDismiss={() => setDownloadError(null)}>
              {downloadError}
            </Callout>
          )}
          {error ? (
            <Empty icon="warning">Files could not load. Try again in a moment.</Empty>
          ) : loading ? (
            <p className="p-8 text-center text-muted">Loading your files.</p>
          ) : files.length === 0 ? (
            <Empty icon="folder">
              {searching
                ? `No files found for “${location.query}”.`
                : location.tab === 'shared'
                  ? 'Nobody has shared a file with you yet.'
                  : 'You have not uploaded a file yet.'}
            </Empty>
          ) : (
            files.map((file) => (
              <button
                key={file.id}
                type="button"
                className="files-row"
                onClick={() => download(file)}
              >
                <Icon name={fileIcon(file.name)} size={20} className="flex-none text-muted" />
                <span className="flex flex-col gap-0.5 min-w-0 grow">
                  <span className="truncate font-strong">{file.name}</span>
                  <span className="flex items-center gap-2 text-xs text-muted">
                    <span>{formatBytes(file.bytes)}</span>
                    {file.updatedAt && <span>{formatWhen(file.updatedAt)}</span>}
                  </span>
                </span>
                {(searching || location.tab === 'shared') && (
                  <span className="flex items-center gap-3 files-row-owner">
                    <Avatar name={file.owner.name} seed={file.owner.subject} />
                    <span className="flex flex-col min-w-0">
                      <span className="text-sm text-muted truncate">{file.owner.name}</span>
                      {roleLabel(file.owner) && (
                        <span className="text-xs text-muted truncate">{roleLabel(file.owner)}</span>
                      )}
                    </span>
                  </span>
                )}
                {!searching && location.tab === 'mine' && (
                  <span className="chip">{audienceLabel(file.audience, teams, companyName)}</span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {uploadFile && (
        <UploadDialog
          file={uploadFile}
          people={people}
          teams={teams}
          onCancel={() => setUploadFile(null)}
          onConfirm={(audience) => handleUpload(uploadFile, audience)}
        />
      )}
    </div>
  );
}
